/**
 * When skillstrike runs embedded in burrito-golf-web's dev Skillstrike panel,
 * multiple iframes share the same origin and therefore the same `localStorage`.
 * PartnerAuth + StoreContextProvider write the active `session_id` and `token`
 * to AsyncStorage (which on web is backed by localStorage), so two iframes
 * loading different test players stomp on each other's session — symptom: the
 * kiosk-updates stream stalls and the bet UI stays disabled in
 * `UserSessionState.UNKNOWN`.
 *
 * To avoid the fight, burrito-golf-web's iframe panel tags every iframe URL
 * with `embedder=burrito-golf-web`. When we see that marker we replace
 * `window.localStorage` with a per-window in-memory store. Each iframe has
 * its own `window`, so each gets its own private store.
 *
 * The marker is checked from `window.location.search` instead of probing the
 * parent frame because cross-origin (burrito-golf-web on :3100, skillstrike
 * on :19006) blocks reads of `window.top.location`. The query-param contract
 * is explicit, verifiable, and only the iframe panel sets it.
 *
 * Standalone use (the regular skillstrike web/native app, or any other
 * embedder) is untouched — no marker, no shim.
 */
const EMBEDDER_PARAM = 'embedder';
const EMBEDDER_VALUE = 'burrito-golf-web';

function isEmbeddedByBurritoGolf(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get(EMBEDDER_PARAM) === EMBEDDER_VALUE;
  } catch {
    return false;
  }
}

function buildMemoryStorage(): Storage {
  const memory = new Map<string, string>();
  return {
    get length() {
      return memory.size;
    },
    clear() {
      memory.clear();
    },
    getItem(key: string) {
      return memory.has(key) ? memory.get(key)! : null;
    },
    key(index: number) {
      return Array.from(memory.keys())[index] ?? null;
    },
    removeItem(key: string) {
      memory.delete(key);
    },
    setItem(key: string, value: string) {
      memory.set(key, String(value));
    },
  };
}

function install(): void {
  if (typeof window === 'undefined' || !isEmbeddedByBurritoGolf()) return;

  const shim = buildMemoryStorage();

  try {
    Object.defineProperty(window, 'localStorage', {
      value: shim,
      configurable: true,
      writable: false,
    });
    console.info(`[iframeStorageShim] in-memory localStorage installed (${EMBEDDER_VALUE} embed)`);
  } catch (err) {
    // Some browsers mark localStorage non-configurable. Fall back to
    // overwriting the methods on the existing object so AsyncStorage still
    // routes through our memory map.
    try {
      const target = window.localStorage;
      target.getItem = shim.getItem.bind(shim);
      target.setItem = shim.setItem.bind(shim);
      target.removeItem = shim.removeItem.bind(shim);
      target.clear = shim.clear.bind(shim);
      target.key = shim.key.bind(shim);
      console.info(`[iframeStorageShim] localStorage methods patched (${EMBEDDER_VALUE} embed, fallback)`);
    } catch (fallbackErr) {
      console.warn('[iframeStorageShim] failed to install', err, fallbackErr);
    }
  }
}

install();
